import React, { useState, useEffect } from 'react';
import { useMutation } from 'react-query';
import useAuth from '../../Hooks/useAuth';
import AxiosUtil from '../../Utils/AxiosUtil';
import {
    Alert as ServaAlert,
    Button,
    Col,
    Divider,
    Form,
    Option,
    Row,
    Select,
    Space,
    Spin,
    Text,
    Title,
} from '@servahealth/serva-ui-kit';
import { SimplifiedUser } from '../Shared/SimplifiedUser';
import { ManagedFTPServer } from '../Shared/ManagedFTPServer';
import { mapFTPServerAdminModel } from '../Shared/ManagedHelpers';
import Report from '../../Interfaces/Responses/Dashboard/Report';
import GetUserAdminModelsRequest from '../../Interfaces/Requests/Administration/GetUserAdminModelsRequest';
import GetUserAdminModelsResponse from '../../Interfaces/Responses/Administration/GetUserAdminModelResponse';
import GetFTPServerAdminModelsRequest from '../../Interfaces/Requests/Administration/GetFTPServerAdminModelsRequest';
import GetFTPServerAdminModelsResponse from '../../Interfaces/Responses/Administration/GetFTPServerAdminModelResponse';
import Administration from '../../Constants/Endpoints/Administration';
import AutoRunTargetFTP from '../../Interfaces/Responses/Schedules/AutoRunTargetFTP';

type AutomaticDeliveryTargetFTPProps = {
    report: Report | undefined;
    autoRunTargetFTP: AutoRunTargetFTP | undefined;
    handleSetADAutoRunTargetFTP: (
        autoRunTargetFTP: AutoRunTargetFTP
    ) => void;
    close: () => void;
};

const AutomaticDeliveryTargetFTP: React.FC<AutomaticDeliveryTargetFTPProps> = ({ close, ...props }) => {

    const { user } = useAuth();
    const [formAutomaticDeliveryTargetFTP] = Form.useForm();

    const [users, setUsers] = useState<SimplifiedUser[]>([]);
    const [ftpServers, setFTPServers] = useState<ManagedFTPServer[]>([]);
    
    const [adReportID, setADReportID] = useState<number | undefined>();
    const [adReportName, setADReportName] = useState<string | undefined>();
    const [adReportGroupName, setADReportGroupName] = useState<string | undefined>();

    const [adUserID, setADUserID] = useState<number | undefined>();
    const [adFTPServerID, setADFTPServerID] = useState<number | undefined>();

    const [currentError, setCurrentError] = useState('');

    useEffect(() => {

        setADReportID(((props.report?.id) && (props.report?.id > 0)) ? props.report.id : 0);
        setADReportName(((props.report?.name) && (props.report?.name !== '')) ? props.report.name : '');
        setADReportGroupName(((props.report?.groupName) && (props.report?.groupName !== '')) ? props.report.groupName : '');

        getUsers(false);
        getFTPServers(false);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { mutate: getUsers, isLoading: isGetUsersLoading, isError: isGetUsersError, error: getUsersError } = useMutation(
        async (showDisabled: boolean) => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_USER_ADMIN_MODELS}`;
                const requestData: GetUserAdminModelsRequest = { gua_includeDisabled: showDisabled, };
                const responseData = AxiosUtil.get<GetUserAdminModelsResponse[], any>(url, requestData);
                return responseData;
            } catch (err) {
                setCurrentError('GET_USER_ADMIN_MODELS: [Get User Admin Models] Internal Error: ' + err);
                console.error('GET_USER_ADMIN_MODELS: [Get User Admin Models] Internal Error: ', err);
                return Promise.reject();
            }
        },
        {
            onError: (err: any) => {
                setCurrentError('GET_USER_ADMIN_MODELS: [Get User Admin Models] Internal Error: ' + err);
                console.error('GET_USER_ADMIN_MODELS: [Get User Admin Models] Internal Error: ', err);
            },
            onSuccess: (response: GetUserAdminModelsResponse[]) => {
                // ---- DEBUG
                // console.log('**** getUsers() response ****');
                // console.log(response);
                // console.log(props.autoRunTargetFTP);
                // ---- END DEBUG

                if (response) {
                    let simplifiedUsers: SimplifiedUser[] = response?.map(
                        (eachElement) => {
                            return {
                                id: eachElement.id,
                                firstName: eachElement.firstName,
                                lastName: eachElement.lastName,
                                email: eachElement.email,
                                password: eachElement.password,
                                userName: eachElement.userName,
                                phone: eachElement.phone,
                                adminComment: eachElement.adminComment,
                                changePassword: eachElement.changePasswordRequired,
                                reportsOnly: eachElement.reportsOnly,
                                isSystemAccount: eachElement.isSystemAccount,
                                isDemo: eachElement.isDemo,
                                securityAdmin: eachElement.securityAdmin,
                                techTeam: eachElement.techTeam,
                                deleted: eachElement.deleted,
                            };
                        }
                    ).sort((a, b) => a.email.localeCompare(b.email));
                    simplifiedUsers = simplifiedUsers.filter(
                        (eachUser) => !eachUser.deleted
                    );
                    setUsers(simplifiedUsers);
                }
            }
        }
    );

    const { mutate: getFTPServers, isLoading: isGetFTPServersLoading, isError: isGetFTPServersError, error: getFTPServersError } = useMutation(
        async (showDisabled: boolean) => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_FTPSERVER_ADMIN_MODELS}`;
                const requestData: GetFTPServerAdminModelsRequest = { };
                const responseData = AxiosUtil.get<GetFTPServerAdminModelsResponse[], any>(url, requestData);
                return responseData;
            } catch (err) {
                setCurrentError('GET_FTPSERVER_ADMIN_MODELS: [Get FTP Server Admin Models AUD] Internal Error: ' + err);
                console.error('GET_FTPSERVER_ADMIN_MODELS: [Get FTP Server Admin Models AUD] Internal Error: ', err);
                return Promise.reject();
            }
        },
        {
            onError: (err: any) => {
                setCurrentError('GET_FTPSERVER_ADMIN_MODELS: [Get FTP Server Admin Models] Internal Error: ' + err);
                console.error('GET_FTPSERVER_ADMIN_MODELS: [Get FTP Server Admin Models] Internal Error: ', err);
            },
            onSuccess: (response: GetFTPServerAdminModelsResponse[]) => {
                // ---- DEBUG
                // console.log('**** getFTPServers() response ****');
                // console.log(response);
                // console.log(props.autoRunTargetFTP);
                // ---- END DEBUG

                if (response) {
                    let simplifiedFTPServers: ManagedFTPServer[] = response?.map(mapFTPServerAdminModel)
                        .sort((a, b) => a.name.localeCompare(b.name));
                    setFTPServers(simplifiedFTPServers);
                }
            }
        }
    );

    const handleADUserIDUpdate = (e: string) => {
        setADUserID(parseInt(e));
    };

    const handleADFTPServerIDUpdate = (e: string) => {
        setADFTPServerID(parseInt(e));
    };

    const onFinish = (values: any) => {
        // ---- DEBUG
        // console.log('ADTF');
        // console.log(values);
        // ---- ENDDEBUG

        let ftpServer = ftpServers.find((ftpServer) => (ftpServer.id === ((values.adFTPServerID as number) ?? 0)));

        const newAutoRunTargetFTP: AutoRunTargetFTP =
        {
            userID: user?.id ?? 0, //???? use input from popup ????
            server: ftpServer?.server ?? '',
            port: ftpServer?.port ?? 0,
            userName: ftpServer?.userName ?? '',
            password: props.autoRunTargetFTP?.password ?? '',
            isSFTP: ftpServer?.isSFTP ?? false,
            format: ftpServer?.format ?? 0,
            delimiter: ftpServer?.delimiter ?? '',
            fixedWidthLayout: ftpServer?.fixedWidthLayout ?? '',
            includeColumnHeaders: ftpServer?.includeColumnHeaders ?? false,
            remoteFolder: ftpServer?.remoteFolder ?? '',
            fileNameQuery: ftpServer?.fileNameQuery ?? '',
            pgp_Key_Name: ftpServer?.pgp_Key_Name ?? '',
            id: props.autoRunTargetFTP?.id ?? 0,
            ftpServerID: (values.adFTPServerID as number) ?? 0,
            autoRunID: props.report?.autoRun.id ?? 0,
            autoRunScheduleID: props.autoRunTargetFTP?.autoRunScheduleID ?? 0,
            autoRunTargetType: props.autoRunTargetFTP?.autoRunTargetType ?? 0,
        };
        // ---- DEBUG
        // console.log(newAutoRunTargetFTP);
        // ---- ENDDEBUG

        props.handleSetADAutoRunTargetFTP(newAutoRunTargetFTP);

        close();
    }
    
    const determineWhichError = () => {
        if ( isGetUsersError ) {
            return currentError + ' ' + (getUsersError as any).response?.data.message;
        }
        else if ( isGetFTPServersError ) {
            return currentError + ' ' + (getFTPServersError as any).response?.data.message;
        }
        else {
            return '';
        }
    } 

    return (
        <>
            <Spin spinning={
                            isGetUsersLoading ||
                            isGetFTPServersLoading ||
                            (currentError !== '')
                           }>
                <div style={{ display: 'table', margin: 'auto', textAlign: 'center', width: '50%' }}>
                {
                    (
                        isGetUsersError || isGetFTPServersError
                    )
                    &&
                    (
                        <ServaAlert
                            className={'mb-2'}
                            message={determineWhichError()}
                            showIcon={true}
                            type="error"
                        ></ServaAlert>
                    )
                }
                </div>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '8px', width: '100%'}}>
                    <Text style={{height: '100%', width: '100%'}}>
                        <Title style={{backgroundColor: 'rgb(00, 55, 90)', borderRadius: '6px', color: 'white', height: '100%', padding: '4px', width: '100%'}} level={4}>Automatic Delivery Target FTP Options</Title>
                    </Text>
                </Space>
                <Form
                    form={formAutomaticDeliveryTargetFTP}
                    initialValues = {{
                        adUserID: ((props.autoRunTargetFTP?.userID === undefined) || (props.autoRunTargetFTP?.userID === 0)) ? user?.runningUser.id : props.autoRunTargetFTP?.userID,
                        adFTPServerID: ((props.autoRunTargetFTP?.ftpServerID === undefined) || (props.autoRunTargetFTP?.ftpServerID === 0)) ? 1 : props.autoRunTargetFTP?.ftpServerID,
                    }}
                    key='automaticDeliveryTargetFTP'
                    onFinish={(values: any) => onFinish(values)}
                    onFinishFailed={(error) => console.log(error)}
                >
                    <Space direction='horizontal' style={{justifyContent: 'center', padding: '8px', width: '100%'}}>
                        <Text italic type='secondary'>{'[' + adReportID + '] ' + adReportGroupName + ': ' + adReportName}</Text>
                    </Space>
                    <Divider style={{padding: 0, margin: 8}}></Divider>
                    <Row gutter={8} style={{ padding: '8px', paddingTop: '2px', paddingBottom: '2px', lineHeight: '50%' }}>
                        <Col span={13}>
                            <div style={{textAlign:'left'}}>
                                <Form.Item
                                    name='adUserID'
                                    style={{ marginBottom: '0px' }}
                                >
                                    <Select
                                        label='User ID:'
                                        value={adUserID}
                                        showSearch
                                        onChange={(e: any) => handleADUserIDUpdate(e)}
                                    >
                                        {users.map(
                                            (eachUser) => {
                                                return (
                                                    <Option
                                                        value={eachUser.id}
                                                        label={eachUser.firstName + ' ' + eachUser.lastName}
                                                    >
                                                        {eachUser.firstName + ' ' + eachUser.lastName + ' (' + eachUser.email + ')'}
                                                    </Option>
                                                );
                                            }
                                        )}
                                    </Select>
                                </Form.Item>
                            </div>
                        </Col>
                        <Col span={1} />
                        <Col span={10}>
                            <div style={{textAlign:'left'}}>
                                <Form.Item
                                    name='adFTPServerID'
                                    style={{ marginBottom: '0px' }}
                                >
                                    <Select
                                        label='FTP Servers:'
                                        value={adFTPServerID}
                                        onChange={(e: any) => handleADFTPServerIDUpdate(e)}
                                    >
                                        {ftpServers.map(
                                            (eachFTPServer) => {
                                                return (
                                                    <Option
                                                        value={eachFTPServer.id}
                                                        label={eachFTPServer.name}
                                                    >
                                                        {eachFTPServer.name + ' (' + eachFTPServer.server + ')'}
                                                    </Option>
                                                );
                                            }
                                        )}
                                    </Select>
                                </Form.Item>
                            </div>
                        </Col>
                    </Row>
                    <Row style={{ padding: '0px', margin: '0px', lineHeight: '50%' }}>
                        <Col span={24}>
                            <Divider style={{ padding: '0px', paddingBottom: '6px', margin: '0px' }}></Divider>
                        </Col>
                    </Row>
                    <Row style={{ padding: '8px', paddingTop: '8px', paddingBottom: '2px', lineHeight: '50%' }}>
                        <Col span={19}></Col>
                        <Col span={2} style={{display: 'flex', justifyContent: 'center'}}>
                            <div style={{ textAlign: 'right' }}>
                                <Form.Item
                                    name="adApplyButton"
                                    style={{ marginBottom: '0px' }}
                                >
                                    <Button
                                        htmlType="submit"
                                        type="primary"
                                    >
                                        Apply
                                    </Button>
                                </Form.Item>
                            </div>
                        </Col>
                        <Col span={1}></Col>
                        <Col span={2} style={{display: 'flex', justifyContent: 'center'}}>
                            <div style={{ textAlign: 'right' }}>
                                <Form.Item
                                    name="adCancelButton"
                                    style={{ marginBottom: '0px' }}
                                >
                                    <Button type="default" onClick={() => close()}>
                                        Cancel
                                    </Button>
                                </Form.Item>
                            </div>
                        </Col>
                    </Row>
                </Form>
            </Spin>
        </>
    );
};

export default AutomaticDeliveryTargetFTP;
